/**
 * Translates an activity type key, trying common variations if the exact key is not found.
 * Handles Coresignal returning slight naming differences from the standard LinkedIn taxonomy
 * (e.g. "&" vs "and", plural/singular, extra words).
 */
export function translateActivityType(
  t: (key: string) => string,
  activityType: string
): string {
  // Try exact match first
  const exact = t(activityType);
  if (exact !== activityType) return exact;

  // Try replacing "&" with "and"
  const withAnd = activityType.replace(/\s*&\s*/g, " and ");
  if (withAnd !== activityType) {
    const result = t(withAnd);
    if (result !== withAnd) return result;
  }

  // Try removing trailing "s" (e.g. "Executive Offices" → "Executive Office")
  if (activityType.endsWith("s")) {
    const singular = activityType.slice(0, -1);
    const result = t(singular);
    if (result !== singular) return result;
  }

  // Try kebab-case conversion (e.g. "Home Health Care Services" → "home-health-care-services")
  const kebab = activityType
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  const kebabResult = t(kebab);
  if (kebabResult !== kebab) return kebabResult;

  // Try kebab-case without trailing "s"
  if (kebab.endsWith("s")) {
    const kebabSingular = kebab.slice(0, -1);
    const result = t(kebabSingular);
    if (result !== kebabSingular) return result;
  }

  // Fallback to original value
  return activityType;
}

/**
 * Ordered revenue ranges for correct sorting.
 * Companies without a range are placed last.
 */
export const REVENUE_RANGE_ORDER: Record<string, number> = {
  "0-100k€": 1,
  "100k-500k€": 2,
  "500k-2M€": 3,
  "2M-10M€": 4,
  "10M-50M€": 5,
  "50M-200M€": 6,
  "200M-1B€": 7,
  "1B+€": 8,
};

/**
 * Ordered headcount ranges for correct sorting.
 * Companies without a range are placed last.
 */
export const HEADCOUNT_RANGE_ORDER: Record<string, number> = {
  "1-10": 1,
  "11-50": 2,
  "51-200": 3,
  "201-500": 4,
  "501-1000": 5,
  "1001-5000": 6,
  "5001-10000": 7,
  "10001+": 8,
};
